/**
 * Character Sheet — play view. MERGED only. Players never see Sheet A/B
 * during play; the split is only on the level-up page.
 *
 * First live write path: HP edits ride the WebSocket $default dispatcher.
 * The page fetches the character roster over the HTTP read path on mount,
 * picks the first character (multi-character pickers are out of scope for
 * this slice), then opens a campaign socket and listens for runtime patches.
 *
 * Mutations are optimistic: the local state moves immediately and the
 * pending version is tracked. The server's echoed patch (or rejection)
 * confirms or replaces it. Rejections reconcile the local state to the
 * server version — the simplest correct strategy until the audit log
 * grows a "retry with replay" path.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CharacterBundle,
  CreateCharacterRequest,
  DEFAULT_SESSION_ID,
  createCharacter,
  getCharacters,
  importCharacter,
  listBackups,
  repairCharacter,
  restoreCharacter,
  updateCharacterOwner,
  type BackupEntry,
  type ImportResponse,
  type RepairResult,
} from '../lib/sessionApi';
import { useAuth } from '../lib/AuthContext';
import { isAdmin } from '../lib/auth';
import { useIdentity } from '../lib/ImpersonationContext';
import { listUsers, type AdminUser } from '../lib/adminApi';
import { CampaignSocket } from '../lib/campaignSocket';
import { reportFrontendError } from '../lib/errorCapture';
import {
  ConditionsUpdateRequest,
  HitDiceUpdateRequest,
  HpUpdateRequest,
  InspirationUpdateRequest,
  InventoryUpdateRequest,
  ResourcesUpdateRequest,
  RuntimeActions,
  RuntimePatchPayload,
  RuntimeRejectedPayload,
  RuntimeServerTypes,
  SlotsUpdateRequest,
  SpellSlotsState,
} from '../lib/runtimeProtocol';
import { totalHp, type Character } from '../lib/character';
import {
  SessionActions,
  SessionServerTypes,
  LogEntryPayload,
  RollRequest,
} from '../lib/sessionProtocol';
import { rollDice, formatRoll } from '../lib/diceEngine';
import { ActiveCondition, InventoryItem, ResourceCounter } from '../lib/runtime';

// Canonical 5e conditions. The runtime type allows any string so campaign
// homebrew can extend this, but the picker UI only surfaces the standard set
// — typing an unknown chip inline isn't wired yet.
const STANDARD_CONDITIONS: readonly string[] = [
  'blinded',
  'charmed',
  'deafened',
  'frightened',
  'grappled',
  'incapacitated',
  'invisible',
  'paralyzed',
  'petrified',
  'poisoned',
  'prone',
  'restrained',
  'stunned',
  'unconscious',
  'exhaustion',
] as const;

// Sheet doesn't render a map but the WS subscribe needs one to bind the
// connection row's SessionId. Reuse the same default the VTT page uses so
// both pages share a session view.
const DEFAULT_MAP_ID = 'local-dev';

interface SheetState {
  bundle: CharacterBundle;
  // Local mirror of the runtime fields the sheet actually drives. Kept
  // separate from the bundle so optimistic updates don't mutate the
  // server-derived snapshot.
  currentHp: number;
  tempHp: number;
  maxHp: number;
  // Per-sheet expended-slot map. Keys are sheet ids ("sheet-a", "sheet-b").
  // Empty object on a freshly seeded character.
  spellSlots: Record<string, SpellSlotsState>;
  // Hit dice spent counter. Max = combined level (Sheet A + Sheet B always
  // level together). Derived client-side for the UI cap; the server
  // trusts the absolute value in the same way it does HP.
  hitDiceSpent: number;
  maxHitDice: number;
  // Binary 5e inspiration flag — you have it or you don't, no stacks.
  inspiration: boolean;
  // Active conditions list. Full-replace on every edit; exhaustion carries
  // a level 1..6 on the row, all other 5e conditions are binary.
  conditions: ActiveCondition[];
  // Class-resource counters — Ki, Channel Divinity, Sorcery Points, etc.
  // Max lives on the ledger (not wired here yet); this just tracks spent.
  resources: ResourceCounter[];
  // Inventory items with equipped/stowed flag. Full-replace on every edit.
  inventory: InventoryItem[];
  version: number;
}

/**
 * Pull combined level out of the (loosely typed) profile so we can compute
 * the hit-dice cap. Both sheets always level together, so combined =
 * sheetA.level * 2. Default 2 so a half-seeded bundle doesn't render a
 * zero-cap counter that looks broken.
 */
function readCombinedLevel(bundle: CharacterBundle): number {
  const profile = bundle.profile as
    | { sheetA?: { level?: number } }
    | null;
  const levelA = profile?.sheetA?.level ?? 1;
  return Math.max(1, levelA * 2);
}

function readMaxHp(bundle: CharacterBundle): number {
  // Use the canonical totalHp derivation when the profile has the fields
  // it needs (base stats + both sheet ledgers). Falls back to the runtime
  // currentHp for partially-loaded bundles.
  const p = bundle.profile;
  if (p?.base && p?.sheetA && p?.sheetB && p?.conSheet) {
    return Math.max(1, totalHp(p as Character));
  }
  return Math.max(1, bundle.runtime?.currentHp ?? 1);
}

function bundleToState(bundle: CharacterBundle): SheetState {
  const r = bundle.runtime ?? {};
  // Runtime is typed as Partial<CharacterRuntime> in sessionApi but the seed
  // writes an empty object for a fresh character — coerce defensively.
  const slots = (r as { spellSlots?: Record<string, SpellSlotsState> }).spellSlots ?? {};
  return {
    bundle,
    currentHp:    r.currentHp ?? 0,
    tempHp:       r.tempHp ?? 0,
    maxHp:        readMaxHp(bundle),
    spellSlots:   slots,
    hitDiceSpent: r.hitDiceSpent ?? 0,
    maxHitDice:   readCombinedLevel(bundle),
    inspiration:  r.inspiration ?? false,
    conditions:   r.conditions ?? [],
    resources:    r.resources ?? [],
    inventory:    r.inventory ?? [],
    version:      r.version ?? 0,
  };
}

export default function Sheet() {
  const { user } = useAuth();
  const { effectiveUser, impersonating } = useIdentity();
  // When impersonating, suppress DM-only UI so the view matches what the
  // player would see. Same pattern as Lore.tsx.
  const dm = !impersonating && isAdmin(user);

  const [allCharacters, setAllCharacters] = useState<CharacterBundle[]>([]);
  const [state, setState] = useState<SheetState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  // Character creation form state (DM only).
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createClassA, setCreateClassA] = useState('');
  const [createClassB, setCreateClassB] = useState('');
  const [createOwnerSub, setCreateOwnerSub] = useState('');
  const [createStats, setCreateStats] = useState<Record<string, number>>({
    STR: 10, DEX: 10, CON: 10, INT: 10, WIS: 10, CHA: 10,
  });
  const [createError, setCreateError] = useState<string | null>(null);
  const [createBusy, setCreateBusy] = useState(false);
  const [players, setPlayers] = useState<AdminUser[]>([]);
  // Repair state
  const [repairBusy, setRepairBusy] = useState(false);
  const [repairResult, setRepairResult] = useState<RepairResult[] | null>(null);
  // Import state
  const [showImport, setShowImport] = useState(false);
  const [importSheet, setImportSheet] = useState<'A' | 'B'>('A');
  const [importPlatform, setImportPlatform] = useState<'ddb' | 'roll20'>('ddb');
  const [importUrl, setImportUrl] = useState('');
  const [importJson, setImportJson] = useState('');
  const [importUpdateBase, setImportUpdateBase] = useState(true);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState<ImportResponse | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  // Restore state
  const [showRestore, setShowRestore] = useState(false);
  const [backups, setBackups] = useState<BackupEntry[]>([]);
  const [restoreBusy, setRestoreBusy] = useState(false);
  // Draft for the "add resource" input. Local-only; never goes to the wire
  // until the user hits Add.
  const [newResourceId, setNewResourceId] = useState('');
  const [newItemName, setNewItemName] = useState('');
  // Dice roller state.
  const [diceExpr, setDiceExpr] = useState('');
  const [lastRoll, setLastRoll] = useState<{ expression: string; results: number[]; total: number; modifier: number } | null>(null);
  const [diceError, setDiceError] = useState<string | null>(null);
  // Session log — recent entries, newest-first. Capped at 50 to avoid
  // unbounded memory growth during long sessions.
  const [logEntries, setLogEntries] = useState<LogEntryPayload[]>([]);
  const socketRef = useRef<CampaignSocket | null>(null);
  // Tracks the version we expect the next server patch to carry. Pending
  // mutations bump this optimistically; rejections snap it back.
  const pendingVersionRef = useRef<number>(0);

  // When impersonating, filter to characters owned by the effective user.
  // When not impersonating (DM view), show all characters.
  const visibleCharacters = impersonating && effectiveUser
    ? allCharacters.filter((c) => c.ownerSub === effectiveUser.sub)
    : allCharacters;

  // Select a character by id from the roster.
  const selectCharacter = useCallback((bundle: CharacterBundle) => {
    const next = bundleToState(bundle);
    setState(next);
    pendingVersionRef.current = next.version;
    setRepairResult(null);
    setImportResult(null);
    setImportError(null);
    setShowImport(false);
    setShowRestore(false);
  }, []);

  // When visible characters change (impersonation switch, owner reassignment,
  // import, repair, etc.), auto-select the first one if the current selection
  // is no longer visible, or re-sync the bundle if the underlying data changed.
  useEffect(() => {
    if (visibleCharacters.length === 0) {
      setState(null);
      return;
    }
    const currentId = state?.bundle.id;
    const match = visibleCharacters.find((c) => c.id === currentId);
    if (!match) {
      selectCharacter(visibleCharacters[0]);
    } else if (match !== state?.bundle) {
      // Same character but the bundle object changed (e.g. owner reassigned,
      // profile updated). Re-derive state while preserving optimistic runtime
      // edits that haven't been confirmed yet.
      setState((prev) => {
        if (!prev || prev.bundle.id !== match.id) return bundleToState(match);
        // Keep in-flight runtime values; only refresh profile-derived fields
        // and the bundle reference (so the owner dropdown picks up the change).
        return {
          ...prev,
          bundle: match,
          maxHp: readMaxHp(match),
          maxHitDice: readCombinedLevel(match),
        };
      });
    }
  }, [visibleCharacters, state?.bundle, selectCharacter]);

  // Load the player list for the DM's owner picker in the creation form.
  useEffect(() => {
    if (!dm) return;
    listUsers().then((resp) => setPlayers(resp.users)).catch(() => {});
  }, [dm]);

  // Handle character creation submission.
  const handleCreate = useCallback(async () => {
    if (!createName.trim() || !createClassA.trim() || !createClassB.trim()) {
      setCreateError('Name, Class A, and Class B are required.');
      return;
    }
    setCreateBusy(true);
    setCreateError(null);
    try {
      const owner = players.find((p) => p.sub === createOwnerSub);
      const req: CreateCharacterRequest = {
        name: createName.trim(),
        classA: createClassA.trim(),
        classB: createClassB.trim(),
        base: createStats,
        ownerSub: createOwnerSub || undefined,
        ownerUsername: owner?.email ?? undefined,
      };
      await createCharacter(req);
      // Re-fetch the roster to pick up the new character.
      const list = await getCharacters(DEFAULT_SESSION_ID);
      setAllCharacters(list.characters);
      // Auto-select the new character (last in the list).
      const newChar = list.characters[list.characters.length - 1];
      if (newChar) selectCharacter(newChar);
      setShowCreate(false);
      setCreateName('');
      setCreateClassA('');
      setCreateClassB('');
      setCreateOwnerSub('');
      setCreateStats({ STR: 10, DEX: 10, CON: 10, INT: 10, WIS: 10, CHA: 10 });
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create character');
    } finally {
      setCreateBusy(false);
    }
  }, [createName, createClassA, createClassB, createOwnerSub, createStats, players, selectCharacter]);

  // Load the roster + open the socket once on mount.
  useEffect(() => {
    let cancelled = false;
    let cs: CampaignSocket | null = null;

    (async () => {
      try {
        const list = await getCharacters(DEFAULT_SESSION_ID);
        if (cancelled) return;
        setAllCharacters(list.characters);
        const first = list.characters[0];
        if (!first) {
          setLoading(false);
          return;
        }
        const initial = bundleToState(first);
        setState(initial);
        pendingVersionRef.current = initial.version;
        setLoading(false);

        cs = await CampaignSocket.connect({
          sessionId: DEFAULT_SESSION_ID,
          mapId: DEFAULT_MAP_ID,
        });
        if (cancelled) {
          cs.close();
          return;
        }
        socketRef.current = cs;

        cs.on<RuntimePatchPayload>(RuntimeServerTypes.RuntimePatch, (payload) => {
          setState((prev) => {
            if (!prev || prev.bundle.id !== payload.characterId) return prev;
            // Only apply patches that move us forward — out-of-order echoes
            // (which shouldn't happen on a single connection but are cheap
            // to guard against) are ignored.
            if (payload.version <= prev.version) return prev;
            pendingVersionRef.current = payload.version;
            return {
              ...prev,
              version:      payload.version,
              currentHp:    payload.currentHp    ?? prev.currentHp,
              tempHp:       payload.tempHp       ?? prev.tempHp,
              spellSlots:   payload.spellSlots   ?? prev.spellSlots,
              hitDiceSpent: payload.hitDiceSpent ?? prev.hitDiceSpent,
              inspiration:  payload.inspiration  ?? prev.inspiration,
              conditions:   payload.conditions   ?? prev.conditions,
              resources:    payload.resources    ?? prev.resources,
              inventory:    payload.inventory    ?? prev.inventory,
            };
          });
          setConflict(null);
        });

        cs.on<RuntimeRejectedPayload>(RuntimeServerTypes.RuntimeRejected, (payload) => {
          setState((prev) => {
            if (!prev || prev.bundle.id !== payload.characterId) return prev;
            pendingVersionRef.current = payload.version;
            return {
              ...prev,
              version:      payload.version,
              currentHp:    payload.currentHp    ?? prev.currentHp,
              tempHp:       payload.tempHp       ?? prev.tempHp,
              spellSlots:   payload.spellSlots   ?? prev.spellSlots,
              hitDiceSpent: payload.hitDiceSpent ?? prev.hitDiceSpent,
              inspiration:  payload.inspiration  ?? prev.inspiration,
              conditions:   payload.conditions   ?? prev.conditions,
              resources:    payload.resources    ?? prev.resources,
              inventory:    payload.inventory    ?? prev.inventory,
            };
          });
          setConflict(`Rejected: ${payload.reason}. Reconciled to v${payload.version}.`);
        });

        // Session log: prepend new entries from the WS broadcast, cap at 50.
        cs.on<LogEntryPayload>(SessionServerTypes.LogEntry, (payload) => {
          setLogEntries((prev) => [payload, ...prev].slice(0, 50));
        });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to open sheet');
          setLoading(false);
        }
        // Caught fetch/socket failures never reach window.onerror, so the
        // global capture hook can't see them. Push them through manually
        // so the live error logger has visibility.
        reportFrontendError(err, { source: 'page-sheet', sessionId: DEFAULT_SESSION_ID });
      }
    })();

    return () => {
      cancelled = true;
      cs?.close();
      socketRef.current = null;
    };
  }, []);

  // Slot mutations ship the FULL new map. The client computes it locally
  // (apply the intended delta on top of the current snapshot), the server
  // overwrites under a version check, and the echoed patch confirms.
  // See SlotsUpdateRequest for why we don't ship per-slot deltas.
  const sendSlotsUpdate = useCallback(
    (nextSlots: Record<string, SpellSlotsState>, reason?: string) => {
      const cs = socketRef.current;
      const cur = state;
      if (!cs || !cur) return;
      const expected = pendingVersionRef.current;
      const req: SlotsUpdateRequest = {
        action: RuntimeActions.SlotsUpdate,
        characterId: cur.bundle.id,
        expectedVersion: expected,
        spellSlots: nextSlots,
        reason,
      };
      cs.send(req);
      pendingVersionRef.current = expected + 1;
      setState((prev) =>
        prev
          ? {
              ...prev,
              spellSlots: nextSlots,
              version:    expected + 1,
            }
          : prev,
      );
    },
    [state],
  );

  const sendHpUpdate = useCallback(
    (nextCurrentHp?: number, nextTempHp?: number, reason?: string) => {
      const cs = socketRef.current;
      const cur = state;
      if (!cs || !cur) return;
      const expected = pendingVersionRef.current;
      const req: HpUpdateRequest = {
        action: RuntimeActions.HpUpdate,
        characterId: cur.bundle.id,
        expectedVersion: expected,
        currentHp: nextCurrentHp,
        tempHp:    nextTempHp,
        reason,
      };
      cs.send(req);
      // Optimistic local apply + optimistic version bump. The server's echo
      // confirms; a rejection snaps both back.
      pendingVersionRef.current = expected + 1;
      setState((prev) =>
        prev
          ? {
              ...prev,
              currentHp: nextCurrentHp ?? prev.currentHp,
              tempHp:    nextTempHp ?? prev.tempHp,
              version:   expected + 1,
            }
          : prev,
      );
    },
    [state],
  );

  // Same pattern as sendHpUpdate / sendSlotsUpdate: optimistic local
  // apply + expected-version bump, echoed runtime/patch confirms, reject
  // snaps back. Ship absolute values, not deltas, so the server stays
  // idempotent and concurrent writes can't double-apply.
  const sendHitDiceUpdate = useCallback(
    (nextSpent: number, reason?: string) => {
      const cs = socketRef.current;
      const cur = state;
      if (!cs || !cur) return;
      const expected = pendingVersionRef.current;
      const req: HitDiceUpdateRequest = {
        action: RuntimeActions.HitDiceUpdate,
        characterId: cur.bundle.id,
        expectedVersion: expected,
        hitDiceSpent: nextSpent,
        reason,
      };
      cs.send(req);
      pendingVersionRef.current = expected + 1;
      setState((prev) =>
        prev
          ? { ...prev, hitDiceSpent: nextSpent, version: expected + 1 }
          : prev,
      );
    },
    [state],
  );

  const sendInspirationUpdate = useCallback(
    (nextInspiration: boolean, reason?: string) => {
      const cs = socketRef.current;
      const cur = state;
      if (!cs || !cur) return;
      const expected = pendingVersionRef.current;
      const req: InspirationUpdateRequest = {
        action: RuntimeActions.InspirationUpdate,
        characterId: cur.bundle.id,
        expectedVersion: expected,
        inspiration: nextInspiration,
        reason,
      };
      cs.send(req);
      pendingVersionRef.current = expected + 1;
      setState((prev) =>
        prev
          ? { ...prev, inspiration: nextInspiration, version: expected + 1 }
          : prev,
      );
    },
    [state],
  );

  // Conditions always ship as the full new list — the client computes the
  // next state locally (add/remove/bump-exhaustion) and the server
  // overwrites under a version check. Same full-replace rationale as slots.
  const sendConditionsUpdate = useCallback(
    (nextConditions: ActiveCondition[], reason?: string) => {
      const cs = socketRef.current;
      const cur = state;
      if (!cs || !cur) return;
      const expected = pendingVersionRef.current;
      const req: ConditionsUpdateRequest = {
        action: RuntimeActions.ConditionsUpdate,
        characterId: cur.bundle.id,
        expectedVersion: expected,
        conditions: nextConditions,
        reason,
      };
      cs.send(req);
      pendingVersionRef.current = expected + 1;
      setState((prev) =>
        prev
          ? { ...prev, conditions: nextConditions, version: expected + 1 }
          : prev,
      );
    },
    [state],
  );

  // Class-resource counters. Full-list replace with the same optimistic
  // apply / expected-version bump / echo-as-ack dance. Max lives on the
  // ledger, not here — this view only drives the spent values.
  const sendResourcesUpdate = useCallback(
    (nextResources: ResourceCounter[], reason?: string) => {
      const cs = socketRef.current;
      const cur = state;
      if (!cs || !cur) return;
      const expected = pendingVersionRef.current;
      const req: ResourcesUpdateRequest = {
        action: RuntimeActions.ResourcesUpdate,
        characterId: cur.bundle.id,
        expectedVersion: expected,
        resources: nextResources,
        reason,
      };
      cs.send(req);
      pendingVersionRef.current = expected + 1;
      setState((prev) =>
        prev
          ? { ...prev, resources: nextResources, version: expected + 1 }
          : prev,
      );
    },
    [state],
  );

  // Inventory mutations. Full-list replace with the same optimistic apply /
  // expected-version bump / echo-as-ack dance. Toggle equipped/stowed, adjust
  // quantity, add/remove items.
  const sendInventoryUpdate = useCallback(
    (nextInventory: InventoryItem[], reason?: string) => {
      const cs = socketRef.current;
      const cur = state;
      if (!cs || !cur) return;
      const expected = pendingVersionRef.current;
      const req: InventoryUpdateRequest = {
        action: RuntimeActions.InventoryUpdate,
        characterId: cur.bundle.id,
        expectedVersion: expected,
        inventory: nextInventory,
        reason,
      };
      cs.send(req);
      pendingVersionRef.current = expected + 1;
      setState((prev) =>
        prev
          ? { ...prev, inventory: nextInventory, version: expected + 1 }
          : prev,
      );
    },
    [state],
  );

  // Roll dice: parse + roll client-side, send result to server for audit + broadcast.
  const doRoll = useCallback(
    (expression: string, reason?: string) => {
      const cs = socketRef.current;
      if (!cs) return;
      try {
        const result = rollDice(expression);
        setLastRoll(result);
        setDiceError(null);
        const req: RollRequest = {
          action: SessionActions.Roll,
          expression: result.expression,
          results: result.results,
          total: result.total,
          reason,
        };
        cs.send(req);
      } catch (err) {
        setDiceError(err instanceof Error ? err.message : 'Invalid expression');
        setLastRoll(null);
      }
    },
    [],
  );

  // Quick-roll helper for the common-die buttons.
  const quickRoll = useCallback(
    (faces: number) => doRoll(`1d${faces}`),
    [doRoll],
  );

  if (loading) return <p>Loading sheet…</p>;
  if (error) return <p style={{ color: 'var(--danger)' }}>Error: {error}</p>;

  // Empty state: no characters at all, or none visible for the impersonated user.
  if (visibleCharacters.length === 0 && !state) {
    return (
      <div className="sheet-page">
        <p style={{ color: 'var(--fg-muted)', marginBottom: 'var(--sp-4)' }}>
          {impersonating
            ? 'This player has no characters assigned yet.'
            : 'No characters in this session yet.'}
        </p>
        {dm && (
          <>
            <button onClick={() => setShowCreate(true)} style={{ marginBottom: 'var(--sp-4)' }}>
              + Create Character
            </button>
            {showCreate && (
              <CharacterCreateForm
                players={players}
                createName={createName} setCreateName={setCreateName}
                createClassA={createClassA} setCreateClassA={setCreateClassA}
                createClassB={createClassB} setCreateClassB={setCreateClassB}
                createOwnerSub={createOwnerSub} setCreateOwnerSub={setCreateOwnerSub}
                createStats={createStats} setCreateStats={setCreateStats}
                createError={createError}
                createBusy={createBusy}
                onSubmit={handleCreate}
                onCancel={() => setShowCreate(false)}
              />
            )}
          </>
        )}
      </div>
    );
  }

  if (!state) return <p>No character.</p>;

  const {
    bundle,
    currentHp,
    tempHp,
    maxHp,
    spellSlots,
    hitDiceSpent,
    maxHitDice,
    inspiration,
    conditions,
    resources,
    inventory,
    version,
  } = state;
  // Lookup by id so add/remove/bump can rebuild the full list in one shot.
  const conditionById = new Map(conditions.map((c) => [c.id, c]));
  const hasCondition = (id: string) => conditionById.has(id);

  // Toggle or remove a binary condition. Exhaustion is handled separately
  // because it carries a level and doesn't round-trip through a plain toggle.
  const toggleBinary = (id: string) => {
    if (hasCondition(id)) {
      sendConditionsUpdate(conditions.filter((c) => c.id !== id), `-${id}`);
    } else {
      sendConditionsUpdate([...conditions, { id }], `+${id}`);
    }
  };

  // Exhaustion ranges 1..6 (5e 2014). Level 0 removes the row entirely.
  const setExhaustion = (level: number) => {
    const without = conditions.filter((c) => c.id !== 'exhaustion');
    if (level <= 0) {
      sendConditionsUpdate(without, 'exhaustion cleared');
      return;
    }
    const clamped = Math.min(6, Math.max(1, level));
    sendConditionsUpdate(
      [...without, { id: 'exhaustion', level: clamped }],
      `exhaustion ${clamped}`,
    );
  };
  const exhaustion = conditionById.get('exhaustion');
  const exhaustionLevel = exhaustion?.level ?? 0;

  // Adjust one resource counter's spent value by a delta and ship the full
  // new list. Clamp at zero — negative spent is meaningless. We can't clamp
  // the upper bound until the ledger wires max into the play view.
  const adjustResource = (id: string, delta: number) => {
    const next = resources.map((r) =>
      r.id === id ? { ...r, spent: Math.max(0, r.spent + delta) } : r,
    );
    sendResourcesUpdate(next, delta > 0 ? `spent ${id}` : `recovered ${id}`);
  };
  const resetResource = (id: string) => {
    const next = resources.map((r) => (r.id === id ? { ...r, spent: 0 } : r));
    sendResourcesUpdate(next, `reset ${id}`);
  };
  const removeResource = (id: string) => {
    sendResourcesUpdate(resources.filter((r) => r.id !== id), `-${id}`);
  };
  const addResource = (id: string) => {
    const trimmed = id.trim();
    if (!trimmed) return;
    if (resources.some((r) => r.id === trimmed)) return;
    sendResourcesUpdate([...resources, { id: trimmed, spent: 0 }], `+${trimmed}`);
  };
  // Inventory helpers.
  const toggleEquipped = (itemId: string) => {
    const next = inventory.map((item) =>
      item.id === itemId ? { ...item, equipped: !item.equipped } : item,
    );
    const target = inventory.find((i) => i.id === itemId);
    const verb = target?.equipped ? 'stow' : 'equip';
    sendInventoryUpdate(next, `${verb} ${target?.name ?? itemId}`);
  };
  const adjustItemQty = (itemId: string, delta: number) => {
    const next = inventory.map((item) =>
      item.id === itemId
        ? { ...item, quantity: Math.max(1, (item.quantity ?? 1) + delta) }
        : item,
    );
    const target = inventory.find((i) => i.id === itemId);
    sendInventoryUpdate(next, `${target?.name ?? itemId} qty ${delta > 0 ? '+' : ''}${delta}`);
  };
  const removeItem = (itemId: string) => {
    const target = inventory.find((i) => i.id === itemId);
    sendInventoryUpdate(
      inventory.filter((i) => i.id !== itemId),
      `-${target?.name ?? itemId}`,
    );
  };
  const addItem = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const id = trimmed.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    if (inventory.some((i) => i.id === id)) return;
    sendInventoryUpdate(
      [...inventory, { id, name: trimmed, quantity: 1, equipped: false }],
      `+${trimmed}`,
    );
  };

  const hitDiceRemaining = Math.max(0, maxHitDice - hitDiceSpent);
  const adjustHitDice = (delta: number) => {
    const next = Math.max(0, Math.min(maxHitDice, hitDiceSpent + delta));
    if (next === hitDiceSpent) return;
    sendHitDiceUpdate(next, delta > 0 ? 'spent' : 'recovered');
  };
  const name = (bundle.profile as { name?: string } | null)?.name ?? bundle.id;

  // Damage and heal apply to currentHp first; temp HP is set explicitly via
  // its own field so a player can dial it after a Heroism cast.
  const adjustHp = (delta: number) => {
    const next = Math.max(-maxHp, Math.min(maxHp, currentHp + delta));
    sendHpUpdate(next, undefined, delta < 0 ? 'damage' : 'heal');
  };

  // Apply a slot delta to one sheet's leveled map and ship the full new map.
  // delta=+1 expends a slot, delta=-1 restores one. We clamp expended >= 0
  // here; the upper bound (max slots) lives in the ledger and isn't wired
  // into the play view yet.
  const adjustSlot = (sheetId: string, level: number, delta: number) => {
    const cur = spellSlots[sheetId] ?? { leveled: {}, pactExpended: null };
    const key = String(level);
    const next = Math.max(0, (cur.leveled[key] ?? 0) + delta);
    const nextSheet: SpellSlotsState = {
      ...cur,
      leveled: { ...cur.leveled, [key]: next },
    };
    sendSlotsUpdate({ ...spellSlots, [sheetId]: nextSheet }, delta > 0 ? 'cast' : 'recover');
  };

  const adjustPact = (sheetId: string, delta: number) => {
    const cur = spellSlots[sheetId];
    if (!cur || cur.pactExpended == null) return;
    const next = Math.max(0, cur.pactExpended + delta);
    sendSlotsUpdate(
      { ...spellSlots, [sheetId]: { ...cur, pactExpended: next } },
      delta > 0 ? 'pact-cast' : 'short-rest',
    );
  };

  const slotSheetIds = Object.keys(spellSlots);

  return (
    <div className="sheet-page">
      {/* Character picker + create button (DM only) */}
      {visibleCharacters.length > 1 && (
        <div className="ctrl-row" style={{ marginBottom: 'var(--sp-3)' }}>
          <select
            value={bundle.id}
            onChange={(e) => {
              const pick = visibleCharacters.find((c) => c.id === e.target.value);
              if (pick) selectCharacter(pick);
            }}
            style={{ minWidth: '160px' }}
          >
            {visibleCharacters.map((c) => {
              const cName = (c.profile as { name?: string } | null)?.name ?? c.id;
              const ownerLabel = c.ownerUsername ? ` (${c.ownerUsername})` : '';
              return (
                <option key={c.id} value={c.id}>
                  {cName}{ownerLabel}
                </option>
              );
            })}
          </select>
        </div>
      )}
      {dm && (
        <div style={{ marginBottom: 'var(--sp-3)' }}>
          <button onClick={() => setShowCreate((v) => !v)} style={{ fontSize: '0.85rem' }}>
            {showCreate ? 'Cancel' : '+ New Character'}
          </button>
        </div>
      )}
      {showCreate && dm && (
        <CharacterCreateForm
          players={players}
          createName={createName} setCreateName={setCreateName}
          createClassA={createClassA} setCreateClassA={setCreateClassA}
          createClassB={createClassB} setCreateClassB={setCreateClassB}
          createOwnerSub={createOwnerSub} setCreateOwnerSub={setCreateOwnerSub}
          createStats={createStats} setCreateStats={setCreateStats}
          createError={createError}
          createBusy={createBusy}
          onSubmit={handleCreate}
          onCancel={() => setShowCreate(false)}
        />
      )}

      <h1>{name}</h1>
      <p style={{ color: 'var(--fg-muted)' }}>
        v{version} · {bundle.id}
        {dm ? (
          <select
            value={bundle.ownerSub ?? ''}
            onChange={async (e) => {
              const newSub = e.target.value || undefined;
              const newUser = players.find((p) => p.sub === newSub);
              try {
                await updateCharacterOwner(
                  bundle.id,
                  newSub ?? null,
                  newUser?.email ?? null,
                );
                // Refresh the character list to pick up the new owner.
                const list = await getCharacters(DEFAULT_SESSION_ID);
                setAllCharacters(list.characters);
              } catch (err) {
                reportFrontendError(err instanceof Error ? err : new Error(String(err)), {
                  action: 'reassign-owner',
                  characterId: bundle.id,
                });
              }
            }}
            style={{ marginLeft: '0.75rem', fontSize: '0.85rem' }}
          >
            <option value="">Unassigned (DM)</option>
            {players.filter((p) => p.enabled).map((p) => (
              <option key={p.sub} value={p.sub}>{p.email ?? p.sub}</option>
            ))}
          </select>
        ) : bundle.ownerUsername ? (
          <span style={{ marginLeft: '0.75rem', color: 'var(--sheet-a)' }}>
            owned by {bundle.ownerUsername}
          </span>
        ) : null}
      </p>

      {dm && (
        <div style={{ marginBottom: 'var(--sp-3)' }}>
          <div className="ctrl-row" style={{ marginBottom: 'var(--sp-2)' }}>
            <button
              disabled={repairBusy}
              onClick={async () => {
                setRepairBusy(true);
                setRepairResult(null);
                try {
                  const resp = await repairCharacter(bundle.id);
                  setRepairResult(resp.repairs);
                  if (resp.repairsApplied > 0) {
                    const list = await getCharacters(DEFAULT_SESSION_ID);
                    setAllCharacters(list.characters);
                  }
                } catch (err) {
                  reportFrontendError(err instanceof Error ? err : new Error(String(err)), {
                    action: 'repair-character', characterId: bundle.id,
                  });
                  setRepairResult([{ fixer: 'error', description: 'Repair failed — check console.' }]);
                } finally {
                  setRepairBusy(false);
                }
              }}
              style={{ fontSize: '0.85rem' }}
            >
              {repairBusy ? 'Repairing…' : 'Fix Character'}
            </button>

            <button
              onClick={() => { setShowImport((v) => !v); setShowRestore(false); }}
              style={{ fontSize: '0.85rem' }}
            >
              {showImport ? 'Cancel Import' : 'Import Sheet'}
            </button>

            <button
              onClick={async () => {
                setShowImport(false);
                setShowRestore((v) => !v);
                if (!showRestore) {
                  try {
                    const resp = await listBackups(bundle.id);
                    setBackups(resp.backups);
                  } catch { setBackups([]); }
                }
              }}
              style={{ fontSize: '0.85rem' }}
            >
              {showRestore ? 'Cancel Restore' : 'Restore Backup'}
            </button>
          </div>

          {repairResult && (
            <ul style={{ margin: '0 0 var(--sp-2) 0', padding: '0 0 0 1.2rem', fontSize: '0.85rem', color: 'var(--fg-muted)' }}>
              {repairResult.length === 0 ? (
                <li>No repairs needed — character is healthy.</li>
              ) : (
                repairResult.map((r, i) => <li key={i}>{r.description}</li>)
              )}
            </ul>
          )}

          {/* ── Import Sheet panel ── */}
          {showImport && (
            <div style={{ padding: 'var(--sp-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginBottom: 'var(--sp-3)' }}>
              <h3 style={{ margin: '0 0 var(--sp-2) 0', fontSize: '1rem' }}>Import from D&D Beyond / Roll20</h3>

              <div style={{ display: 'flex', gap: 'var(--sp-3)', marginBottom: 'var(--sp-2)', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '0.85rem' }}>
                  Target sheet
                  <select value={importSheet} onChange={(e) => setImportSheet(e.target.value as 'A' | 'B')}
                    style={{ display: 'block', marginTop: '2px' }}>
                    <option value="A">Sheet A</option>
                    <option value="B">Sheet B</option>
                  </select>
                </label>

                <label style={{ fontSize: '0.85rem' }}>
                  Platform
                  <select value={importPlatform} onChange={(e) => setImportPlatform(e.target.value as 'ddb' | 'roll20')}
                    style={{ display: 'block', marginTop: '2px' }}>
                    <option value="ddb">D&D Beyond</option>
                    <option value="roll20">Roll20</option>
                  </select>
                </label>

                <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem', alignSelf: 'flex-end' }}>
                  <input type="checkbox" checked={importUpdateBase}
                    onChange={(e) => setImportUpdateBase(e.target.checked)} />
                  Update base stats
                </label>
              </div>

              {importPlatform === 'ddb' && (
                <label style={{ display: 'block', marginBottom: 'var(--sp-2)', fontSize: '0.85rem' }}>
                  D&D Beyond URL (public character)
                  <input type="text" value={importUrl}
                    onChange={(e) => setImportUrl(e.target.value)}
                    placeholder="https://www.dndbeyond.com/characters/12345678"
                    style={{ display: 'block', width: '100%', marginTop: '2px' }} />
                </label>
              )}

              <label style={{ display: 'block', marginBottom: 'var(--sp-2)', fontSize: '0.85rem' }}>
                {importPlatform === 'ddb' ? 'Or paste character JSON (fallback)' : 'Paste character JSON (Roll20 export)'}
                <textarea value={importJson}
                  onChange={(e) => setImportJson(e.target.value)}
                  rows={4}
                  placeholder='{"data": { ... }}'
                  style={{ display: 'block', width: '100%', marginTop: '2px', fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }} />
              </label>

              <button
                disabled={importBusy || (!importUrl && !importJson)}
                onClick={async () => {
                  setImportBusy(true);
                  setImportResult(null);
                  setImportError(null);
                  try {
                    let data: unknown | undefined;
                    if (importJson.trim()) {
                      try { data = JSON.parse(importJson.trim()); }
                      catch { setImportError('Invalid JSON'); setImportBusy(false); return; }
                    }
                    const resp = await importCharacter(bundle.id, {
                      sheet: importSheet,
                      platform: importPlatform,
                      url: importUrl || undefined,
                      data,
                      updateBase: importUpdateBase,
                    });
                    setImportResult(resp);
                    const list = await getCharacters(DEFAULT_SESSION_ID);
                    setAllCharacters(list.characters);
                  } catch (err) {
                    const msg = err instanceof Error ? err.message : String(err);
                    setImportError(msg);
                    reportFrontendError(err instanceof Error ? err : new Error(msg), {
                      action: 'import-character', characterId: bundle.id,
                    });
                  } finally {
                    setImportBusy(false);
                  }
                }}
                style={{ fontSize: '0.85rem' }}
              >
                {importBusy ? 'Importing…' : 'Import'}
              </button>

              {importError && (
                <p style={{ color: 'var(--danger)', fontSize: '0.85rem', marginTop: 'var(--sp-2)' }}>{importError}</p>
              )}
              {importResult && (
                <div style={{ marginTop: 'var(--sp-2)', fontSize: '0.85rem', color: 'var(--fg-muted)' }}>
                  <p style={{ margin: 0 }}>
                    Imported <strong>{importResult.imported.className}</strong>
                    {importResult.imported.subclass && ` (${importResult.imported.subclass})`}
                    {' '}level {importResult.imported.level} → Sheet {importResult.sheet}.
                    Max HP: {importResult.maxHp}. {importResult.imported.proficiencyCount} proficiencies, {importResult.imported.feats.length} feats.
                  </p>
                  {importResult.warnings.length > 0 && (
                    <ul style={{ margin: 'var(--sp-1) 0 0 0', padding: '0 0 0 1.2rem', color: 'var(--warning, var(--sheet-a))' }}>
                      {importResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Restore Backup panel ── */}
          {showRestore && (
            <div style={{ padding: 'var(--sp-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginBottom: 'var(--sp-3)' }}>
              <h3 style={{ margin: '0 0 var(--sp-2) 0', fontSize: '1rem' }}>Restore from Backup</h3>
              {backups.length === 0 ? (
                <p style={{ fontSize: '0.85rem', color: 'var(--fg-muted)' }}>No backups found for this character.</p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {backups.slice(0, 10).map((b) => (
                    <li key={b.timestamp} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--sp-1) 0', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}>
                      <span className="numeric" style={{ color: 'var(--fg-muted)' }}>
                        {b.timestamp.replace(/T/, ' ').replace(/Z$/, ' UTC')}
                      </span>
                      <button
                        disabled={restoreBusy}
                        onClick={async () => {
                          setRestoreBusy(true);
                          try {
                            await restoreCharacter(bundle.id, b.timestamp);
                            const list = await getCharacters(DEFAULT_SESSION_ID);
                            setAllCharacters(list.characters);
                            setShowRestore(false);
                          } catch (err) {
                            reportFrontendError(err instanceof Error ? err : new Error(String(err)), {
                              action: 'restore-character', characterId: bundle.id,
                            });
                          } finally {
                            setRestoreBusy(false);
                          }
                        }}
                        style={{ fontSize: '0.8rem' }}
                      >
                        Restore
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button
                disabled={restoreBusy}
                onClick={async () => {
                  setRestoreBusy(true);
                  try {
                    await restoreCharacter(bundle.id);
                    const list = await getCharacters(DEFAULT_SESSION_ID);
                    setAllCharacters(list.characters);
                    setShowRestore(false);
                  } catch (err) {
                    reportFrontendError(err instanceof Error ? err : new Error(String(err)), {
                      action: 'restore-character-latest', characterId: bundle.id,
                    });
                  } finally {
                    setRestoreBusy(false);
                  }
                }}
                style={{ fontSize: '0.85rem', marginTop: 'var(--sp-2)' }}
              >
                {restoreBusy ? 'Restoring…' : 'Restore Latest'}
              </button>
            </div>
          )}
        </div>
      )}

      <section>
        <h2>Hit Points</h2>
        <div className="numeric" style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>
          <span style={{ color: currentHp <= 0 ? 'var(--danger)' : 'var(--fg-base)' }}>
            {currentHp}
          </span>
          <span style={{ color: 'var(--fg-muted)' }}> / {maxHp}</span>
          {tempHp > 0 && (
            <span style={{ color: 'var(--sheet-b)', marginLeft: '0.75rem' }}>
              +{tempHp} temp
            </span>
          )}
        </div>

        <div className="ctrl-row">
          {[1, 5, 10].map((d) => (
            <button key={`dmg-${d}`} onClick={() => adjustHp(-d)}>
              -{d}
            </button>
          ))}
          {[1, 5, 10].map((d) => (
            <button key={`heal-${d}`} onClick={() => adjustHp(d)}>
              +{d}
            </button>
          ))}
          <button onClick={() => sendHpUpdate(maxHp, 0, 'long rest')}>Full heal</button>
        </div>

        <div className="ctrl-row">
          <label htmlFor="tempHp" style={{ color: 'var(--fg-muted)' }}>
            Temp HP
          </label>
          <input
            id="tempHp"
            type="number"
            min={0}
            value={tempHp}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              if (Number.isFinite(v)) sendHpUpdate(undefined, Math.max(0, v));
            }}
            style={{ width: '4rem' }}
          />
        </div>
      </section>

      <section>
        <h2>Hit Dice</h2>
        <div className="ctrl-row" style={{ marginBottom: '0.5rem' }}>
          <span className="numeric" style={{ fontSize: '1.25rem' }}>
            <span style={{ color: hitDiceRemaining === 0 ? 'var(--danger)' : 'var(--fg-base)' }}>
              {hitDiceRemaining}
            </span>
            <span style={{ color: 'var(--fg-muted)' }}> / {maxHitDice}</span>
          </span>
          <span style={{ color: 'var(--fg-muted)', fontSize: '0.85rem' }}>
            remaining
          </span>
        </div>
        <div className="ctrl-row">
          <button onClick={() => adjustHitDice(+1)} disabled={hitDiceRemaining === 0}>
            Spend one
          </button>
          <button onClick={() => adjustHitDice(-1)} disabled={hitDiceSpent === 0}>
            Recover one
          </button>
          <button
            onClick={() => sendHitDiceUpdate(0, 'long rest')}
            disabled={hitDiceSpent === 0}
          >
            Long rest (reset)
          </button>
        </div>
      </section>

      <section>
        <h2>Inspiration</h2>
        {/*
         * Binary flag — you have it or you don't. Rendered as a toggle
         * button rather than a checkbox because the "spent" and "gained"
         * actions feel like distinct deliberate moves at the table, not a
         * fiddly form control.
         */}
        <div className="ctrl-row">
          <span
            style={{
              display: 'inline-block',
              width: '0.9rem',
              height: '0.9rem',
              borderRadius: '50%',
              background: inspiration ? 'var(--sheet-a)' : 'transparent',
              border: '2px solid var(--sheet-a)',
            }}
          />
          <span style={{ color: inspiration ? 'var(--fg-base)' : 'var(--fg-muted)' }}>
            {inspiration ? 'Have inspiration' : 'No inspiration'}
          </span>
          {inspiration ? (
            <button onClick={() => sendInspirationUpdate(false, 'spent')}>
              Spend
            </button>
          ) : (
            <button onClick={() => sendInspirationUpdate(true, 'granted')}>
              Grant
            </button>
          )}
        </div>
      </section>

      <section>
        <h2>Conditions</h2>
        {/*
         * Active-first chip row, then a toggle row of everything else.
         * Exhaustion is pulled out into its own stepper because it carries
         * a 1..6 level the rest of the conditions don't have. Homebrew
         * condition strings aren't wired here yet — the picker only
         * surfaces STANDARD_CONDITIONS.
         */}
        {conditions.length === 0 ? (
          <p style={{ color: 'var(--fg-muted)', fontStyle: 'italic', margin: '0 0 0.5rem' }}>
            None
          </p>
        ) : (
          <div className="chip-row">
            {conditions.map((c) => (
              <span
                key={c.id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  padding: '0.2rem 0.55rem',
                  borderRadius: '999px',
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border)',
                  fontSize: '0.85rem',
                }}
              >
                {c.id}
                {c.level != null && (
                  <span style={{ color: 'var(--fg-muted)' }}>· {c.level}</span>
                )}
                <button
                  onClick={() =>
                    c.id === 'exhaustion'
                      ? setExhaustion(0)
                      : toggleBinary(c.id)
                  }
                  aria-label={`Remove ${c.id}`}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--fg-muted)',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: '1rem',
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="chip-row">
          {STANDARD_CONDITIONS.filter((id) => id !== 'exhaustion').map((id) => {
            const active = hasCondition(id);
            return (
              <button
                key={id}
                onClick={() => toggleBinary(id)}
                style={{
                  opacity: active ? 1 : 0.6,
                  fontWeight: active ? 600 : 400,
                }}
              >
                {active ? '−' : '+'} {id}
              </button>
            );
          })}
        </div>

        <div className="ctrl-row">
          <span style={{ color: 'var(--fg-muted)', fontSize: '0.85rem' }}>Exhaustion</span>
          <button onClick={() => setExhaustion(exhaustionLevel - 1)} disabled={exhaustionLevel === 0}>
            −
          </button>
          <span className="numeric" style={{ minWidth: '1.5rem', textAlign: 'center' }}>
            {exhaustionLevel}
          </span>
          <button onClick={() => setExhaustion(exhaustionLevel + 1)} disabled={exhaustionLevel === 6}>
            +
          </button>
          <span style={{ color: 'var(--fg-muted)', fontSize: '0.85rem' }}>/ 6</span>
        </div>
      </section>

      <section>
        <h2>Class Resources</h2>
        {/*
         * Generic class-feature counters — Ki, Channel Divinity, Sorcery
         * Points, Bardic Inspiration, Lay on Hands, etc. The runtime row
         * only carries spent counts; max lives on the ledger and isn't
         * wired here yet. Until then, the UI shows "spent: N" with +/-
         * and a reset, and relies on the player to know their own cap.
         *
         * Add-resource input lets the player bootstrap a counter by id
         * (e.g. "ki") without the level-up page having seeded it.
         */}
        {resources.length === 0 ? (
          <p style={{ color: 'var(--fg-muted)', fontStyle: 'italic', margin: '0 0 0.5rem' }}>
            No resources tracked yet.
          </p>
        ) : (
          <div style={{ marginBottom: '0.5rem' }}>
            {resources.map((r) => (
              <div key={r.id} className="ctrl-row">
                <span style={{ minWidth: '9rem' }}>{r.id}</span>
                <button onClick={() => adjustResource(r.id, -1)} disabled={r.spent === 0}>
                  −
                </button>
                <span className="numeric" style={{ minWidth: '2rem', textAlign: 'center' }}>
                  {r.spent}
                </span>
                <button onClick={() => adjustResource(r.id, +1)}>+</button>
                <span style={{ color: 'var(--fg-muted)' }}>spent</span>
                <button
                  onClick={() => resetResource(r.id)}
                  disabled={r.spent === 0}
                  style={{ marginLeft: '0.5rem' }}
                >
                  Reset
                </button>
                <button
                  onClick={() => removeResource(r.id)}
                  aria-label={`Remove ${r.id}`}
                  style={{ color: 'var(--fg-muted)' }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            addResource(newResourceId);
            setNewResourceId('');
          }}
          className="ctrl-row"
        >
          <input
            type="text"
            placeholder="ki, channel-divinity, …"
            value={newResourceId}
            onChange={(e) => setNewResourceId(e.target.value)}
            style={{ flex: '0 1 14rem' }}
          />
          <button type="submit" disabled={!newResourceId.trim()}>
            Add counter
          </button>
        </form>
      </section>

      <section>
        <h2>Inventory</h2>
        {inventory.length === 0 ? (
          <p style={{ color: 'var(--fg-muted)', fontStyle: 'italic', margin: '0 0 0.5rem' }}>
            No items yet.
          </p>
        ) : (
          <div style={{ marginBottom: '0.5rem' }}>
            {inventory.map((item) => (
              <div
                key={item.id}
                className="inv-row"
                style={{ opacity: item.equipped ? 1 : 0.7 }}
              >
                <button
                  onClick={() => toggleEquipped(item.id)}
                  title={item.equipped ? 'Equipped — click to stow' : 'Stowed — click to equip'}
                  style={{
                    background: item.equipped ? 'var(--sheet-b)' : 'transparent',
                    border: `2px solid ${item.equipped ? 'var(--sheet-b)' : 'var(--border)'}`,
                    borderRadius: '4px',
                    width: '1.3rem',
                    height: '1.3rem',
                    cursor: 'pointer',
                    padding: 0,
                    flexShrink: 0,
                    minWidth: 'auto',
                    minHeight: 'auto',
                  }}
                  aria-label={item.equipped ? `Stow ${item.name}` : `Equip ${item.name}`}
                />
                <span style={{ minWidth: '7rem', flex: '1 1 0' }}>
                  {item.name}
                </span>
                <button
                  onClick={() => adjustItemQty(item.id, -1)}
                  disabled={(item.quantity ?? 1) <= 1}
                  aria-label={`Decrease ${item.name} quantity`}
                >
                  −
                </button>
                <span className="numeric" style={{ minWidth: '2rem', textAlign: 'center' }}>
                  {item.quantity ?? 1}
                </span>
                <button
                  onClick={() => adjustItemQty(item.id, +1)}
                  aria-label={`Increase ${item.name} quantity`}
                >
                  +
                </button>
                <span style={{ color: 'var(--fg-muted)', fontSize: '0.8rem' }}>
                  {item.equipped ? 'equipped' : 'stowed'}
                </span>
                {item.attuned && (
                  <span style={{ color: 'var(--sheet-a)', fontSize: '0.8rem' }}>
                    attuned
                  </span>
                )}
                <button
                  onClick={() => removeItem(item.id)}
                  aria-label={`Remove ${item.name}`}
                  style={{ color: 'var(--fg-muted)', marginLeft: 'auto' }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            addItem(newItemName);
            setNewItemName('');
          }}
          className="ctrl-row"
        >
          <input
            type="text"
            placeholder="longsword, shield, …"
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
            style={{ flex: '0 1 14rem' }}
          />
          <button type="submit" disabled={!newItemName.trim()}>
            Add item
          </button>
        </form>
      </section>

      <section>
        <h2>Spell Slots</h2>
        {slotSheetIds.length === 0 && (
          <p style={{ color: 'var(--fg-muted)' }}>
            No slot data yet. Slots populate after the level-up page seeds them.
          </p>
        )}
        {slotSheetIds.map((sid) => {
          const sheet = spellSlots[sid];
          const levels = Object.keys(sheet.leveled)
            .map((k) => parseInt(k, 10))
            .filter((n) => Number.isFinite(n))
            .sort((a, b) => a - b);
          return (
            <div key={sid} style={{ marginBottom: '0.75rem' }}>
              <div style={{ color: 'var(--fg-muted)', marginBottom: '0.25rem' }}>{sid}</div>
              {levels.length === 0 && (
                <div style={{ color: 'var(--fg-muted)', fontStyle: 'italic' }}>
                  no leveled slots
                </div>
              )}
              {levels.map((lvl) => (
                <div key={`${sid}-${lvl}`} className="ctrl-row">
                  <span style={{ width: '5rem' }}>Level {lvl}</span>
                  <button onClick={() => adjustSlot(sid, lvl, -1)}>-</button>
                  <span className="numeric" style={{ minWidth: '2rem', textAlign: 'center' }}>
                    {sheet.leveled[String(lvl)] ?? 0}
                  </span>
                  <button onClick={() => adjustSlot(sid, lvl, +1)}>+</button>
                  <span style={{ color: 'var(--fg-muted)' }}>expended</span>
                </div>
              ))}
              {sheet.pactExpended != null && (
                <div className="ctrl-row" style={{ marginTop: '0.25rem' }}>
                  <span style={{ width: '5rem' }}>Pact</span>
                  <button onClick={() => adjustPact(sid, -1)}>-</button>
                  <span className="numeric" style={{ minWidth: '2rem', textAlign: 'center' }}>
                    {sheet.pactExpended}
                  </span>
                  <button onClick={() => adjustPact(sid, +1)}>+</button>
                  <span style={{ color: 'var(--fg-muted)' }}>expended</span>
                </div>
              )}
            </div>
          );
        })}
      </section>

      <section>
        <h2>Dice Roller</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (diceExpr.trim()) {
              doRoll(diceExpr.trim());
              setDiceExpr('');
            }
          }}
          className="ctrl-row"
        >
          <input
            type="text"
            placeholder="2d6+3, 1d20+5, …"
            value={diceExpr}
            onChange={(e) => setDiceExpr(e.target.value)}
            style={{ flex: '0 1 14rem' }}
          />
          <button type="submit" disabled={!diceExpr.trim()}>
            Roll
          </button>
        </form>

        <div className="chip-row">
          {[20, 4, 6, 8, 10, 12].map((faces) => (
            <button key={`qr-${faces}`} onClick={() => quickRoll(faces)}>
              d{faces}
            </button>
          ))}
        </div>

        {diceError && (
          <p style={{ color: 'var(--danger)', margin: '0.25rem 0' }}>{diceError}</p>
        )}

        {lastRoll && (
          <div
            style={{
              padding: '0.5rem 0.75rem',
              borderRadius: '6px',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              marginBottom: '0.25rem',
            }}
          >
            <div style={{ fontSize: '1.25rem', marginBottom: '0.15rem' }}>
              <span className="numeric">{lastRoll.total}</span>
            </div>
            <div style={{ color: 'var(--fg-muted)', fontSize: '0.85rem' }}>
              {formatRoll(lastRoll)}
            </div>
          </div>
        )}
      </section>

      <section>
        <h2>Session Log</h2>
        {logEntries.length === 0 ? (
          <p style={{ color: 'var(--fg-muted)', fontStyle: 'italic' }}>
            No events yet. Roll some dice!
          </p>
        ) : (
          <div className="session-log">
            {logEntries.map((entry) => (
              <div key={entry.id} className="session-log__entry">
                <span
                  style={{
                    color: entry.kind === 'roll'
                      ? 'var(--sheet-b)'
                      : 'var(--fg-muted)',
                    fontWeight: 600,
                    minWidth: '1.6rem',
                    textAlign: 'center',
                  }}
                  title={entry.kind}
                >
                  {entry.kind === 'roll' ? '\u{1F3B2}' : '\u{1F4DD}'}
                </span>
                <span style={{ color: 'var(--fg-muted)', minWidth: '5rem' }}>
                  {entry.actorUsername}
                </span>
                <span style={{ flex: 1 }}>
                  {entry.summary}
                </span>
                <span style={{ color: 'var(--fg-muted)', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                  {new Date(entry.ts).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {conflict && (
        <p style={{ color: 'var(--warning)', marginTop: '1rem' }}>{conflict}</p>
      )}
    </div>
  );
}

// ---- Character creation form (DM only) ----

const ABILITY_SCORES = ['STR', 'DEX', 'CON', 'INT', 'WIS', 'CHA'] as const;

interface CreateFormProps {
  players: AdminUser[];
  createName: string; setCreateName: (v: string) => void;
  createClassA: string; setCreateClassA: (v: string) => void;
  createClassB: string; setCreateClassB: (v: string) => void;
  createOwnerSub: string; setCreateOwnerSub: (v: string) => void;
  createStats: Record<string, number>; setCreateStats: (v: Record<string, number>) => void;
  createError: string | null;
  createBusy: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}

function CharacterCreateForm({
  players,
  createName, setCreateName,
  createClassA, setCreateClassA,
  createClassB, setCreateClassB,
  createOwnerSub, setCreateOwnerSub,
  createStats, setCreateStats,
  createError,
  createBusy,
  onSubmit,
  onCancel,
}: CreateFormProps) {
  const setStat = (stat: string, val: number) => {
    setCreateStats({ ...createStats, [stat]: Math.max(1, Math.min(30, val)) });
  };

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
      style={{
        background: 'var(--bg-raised)',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        padding: 'var(--sp-4)',
        marginBottom: 'var(--sp-4)',
        maxWidth: '480px',
      }}
    >
      <h3 style={{ marginTop: 0 }}>New Character</h3>

      <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
        <span style={{ color: 'var(--fg-muted)', fontSize: '0.85rem' }}>Name</span>
        <input
          type="text"
          value={createName}
          onChange={(e) => setCreateName(e.target.value)}
          placeholder="e.g. Saru"
          style={{ display: 'block', width: '100%', marginTop: '2px' }}
          autoFocus
        />
      </label>

      <div className="ctrl-row" style={{ marginBottom: 'var(--sp-3)' }}>
        <label style={{ flex: 1 }}>
          <span style={{ color: 'var(--fg-muted)', fontSize: '0.85rem' }}>Sheet A Class</span>
          <input
            type="text"
            value={createClassA}
            onChange={(e) => setCreateClassA(e.target.value)}
            placeholder="e.g. Paladin"
            style={{ display: 'block', width: '100%', marginTop: '2px' }}
          />
        </label>
        <label style={{ flex: 1 }}>
          <span style={{ color: 'var(--fg-muted)', fontSize: '0.85rem' }}>Sheet B Class</span>
          <input
            type="text"
            value={createClassB}
            onChange={(e) => setCreateClassB(e.target.value)}
            placeholder="e.g. Warlock"
            style={{ display: 'block', width: '100%', marginTop: '2px' }}
          />
        </label>
      </div>

      <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
        <span style={{ color: 'var(--fg-muted)', fontSize: '0.85rem' }}>Owner (player)</span>
        <select
          value={createOwnerSub}
          onChange={(e) => setCreateOwnerSub(e.target.value)}
          style={{ display: 'block', width: '100%', marginTop: '2px' }}
        >
          <option value="">Unassigned (DM)</option>
          {players.filter((p) => p.enabled).map((p) => (
            <option key={p.sub} value={p.sub}>{p.email ?? p.sub}</option>
          ))}
        </select>
      </label>

      <fieldset style={{ border: '1px solid var(--border)', borderRadius: '6px', padding: 'var(--sp-2) var(--sp-3)', marginBottom: 'var(--sp-3)' }}>
        <legend style={{ color: 'var(--fg-muted)', fontSize: '0.85rem' }}>Base Ability Scores</legend>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--sp-2)' }}>
          {ABILITY_SCORES.map((stat) => (
            <label key={stat} style={{ textAlign: 'center' }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--fg-muted)' }}>{stat}</span>
              <input
                type="number"
                min={1}
                max={30}
                value={createStats[stat] ?? 10}
                onChange={(e) => setStat(stat, parseInt(e.target.value, 10) || 10)}
                className="numeric"
                style={{ display: 'block', width: '100%', textAlign: 'center' }}
              />
            </label>
          ))}
        </div>
      </fieldset>

      {createError && (
        <p style={{ color: 'var(--danger)', fontSize: '0.85rem', margin: '0 0 var(--sp-2)' }}>
          {createError}
        </p>
      )}

      <div className="ctrl-row">
        <button type="submit" disabled={createBusy}>
          {createBusy ? 'Creating…' : 'Create Character'}
        </button>
        <button type="button" onClick={onCancel} disabled={createBusy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
